/**
 * Deep equality check for objects and arrays.
 * For primitives, uses === comparison.
 * Handles special cases like Date objects, RegExp, Map, Set, etc.
 *
 * @param a First value to compare
 * @param b Second value to compare
 * @returns true if values are deeply equal, false otherwise
 */
export function deepEqual(a: any, b: any): boolean {
  // Strict equality check (handles primitives, same reference, NaN edge case)
  if (a === b) return true;

  // Handle null/undefined
  if (a == null || b == null) return false;

  // Handle non-objects (primitives that aren't equal)
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  // Handle Date objects - compare by time value since Date has no enumerable keys
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }
  // If only one is a Date, they're not equal
  if (a instanceof Date || b instanceof Date) {
    return false;
  }

  // Handle RegExp objects
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.source === b.source && a.flags === b.flags;
  }
  if (a instanceof RegExp || b instanceof RegExp) {
    return false;
  }

  // Handle Map objects
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    for (const [key, value] of a) {
      if (!b.has(key) || !deepEqual(value, b.get(key))) return false;
    }
    return true;
  }
  if (a instanceof Map || b instanceof Map) {
    return false;
  }

  // Handle Set objects
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    for (const value of a) {
      if (!b.has(value)) return false;
    }
    return true;
  }
  if (a instanceof Set || b instanceof Set) {
    return false;
  }

  // Handle Array vs non-Array mismatch
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  // Handle arrays
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Handle plain objects
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}
